import React, { useContext } from "react";
import {
  Box,
  Button,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { DataContext } from "../context/DataContext";
import useLocale from "../hooks/useLocale";

// Animated red warning chip — same pop + stroke-draw pattern as
// the ResultScreen's success / failure chip, but the mark is the
// alert exclamation (line + dot). Keeps the visual language
// consistent across all terminal/transition screens.
function AnimatedWarningChip({ size = 48 }) {
  const theme = useMantineTheme();
  const tint = theme.colors.red[1];
  const accent = theme.colors.red[6];

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@keyframes warningChipPop": {
          "0%": { transform: "scale(0.4)", opacity: 0 },
          "60%": { transform: "scale(1.08)", opacity: 1 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
        "@keyframes warningChipDrawLine": {
          "0%": { strokeDashoffset: 30 },
          "100%": { strokeDashoffset: 0 },
        },
        "@keyframes warningChipDotIn": {
          "0%": { opacity: 0, transform: "scale(0.4)" },
          "100%": { opacity: 1, transform: "scale(1)" },
        },
        animation:
          "warningChipPop 460ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        style={{ display: "block" }}
      >
        <circle cx="50" cy="50" r="48" fill={tint} />
        {/* Vertical exclamation line strokes in first */}
        <path
          d="M50 28 L50 58"
          fill="none"
          stroke={accent}
          strokeWidth="8"
          strokeLinecap="round"
          style={{
            strokeDasharray: 30,
            strokeDashoffset: 30,
            animation:
              "warningChipDrawLine 300ms ease-out 360ms forwards",
          }}
        />
        {/* Dot pops in last, after the line settles */}
        <circle
          cx="50"
          cy="72"
          r="4.5"
          fill={accent}
          style={{
            transformOrigin: "50px 72px",
            opacity: 0,
            animation:
              "warningChipDotIn 220ms cubic-bezier(0.34, 1.56, 0.64, 1) 680ms forwards",
          }}
        />
      </svg>
    </Box>
  );
}

export default function VerificationTransition({ onContinue }) {
  const { data } = useContext(DataContext);
  const locale = useLocale("verification-transition");

  const buttonContentColor =
    data?.styles?.components?.button?.color || undefined;

  const title = locale?.title || "Additional Verification Needed";
  const subtitle =
    locale?.subtitle ||
    "The previous verification step was unsuccessful. Don't worry — we'll guide you through an alternative method to complete your verification.";
  const buttonText = locale?.buttonText || "Continue";

  return (
    <Box
      style={{
        // Fills the App.js shell — `.content` and the inner div
        // already supply the outer padding, so we just provide a
        // top-aligned column with the button anchored at the bottom.
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing="md" align="flex-start">
        <AnimatedWarningChip size={48} />
        <Stack spacing={6}>
          <Text fz={24} fw={700}>
            {title}
          </Text>
          <Text c="dimmed" fz="md">
            {subtitle}
          </Text>
        </Stack>
      </Stack>

      <Box style={{ flex: 1 }} />

      <Button
        fullWidth
        size="lg"
        onClick={onContinue}
        styles={{
          root: { color: buttonContentColor || "#fff" },
        }}
      >
        {buttonText}
      </Button>
    </Box>
  );
}
